import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  RestartAlt as InitializeIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const COUNTRIES = ['India', 'Vietnam', 'Indonesia'];
const CATEGORIES = ['Identity', 'Education', 'Financial', 'Policy', 'Experience', 'Other'];

const AdminDocumentRequirements = () => {
  const { tokenManager } = useAuth();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('India');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [formData, setFormData] = useState({
    country: 'India',
    documentId: '',
    label: '',
    description: '',
    required: true,
    category: 'Other',
    order: 0,
    imageOnly: false,
    internRequired: false,
  });

  useEffect(() => {
    fetchRequirements();
  }, []);

  const fetchRequirements = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/document-requirements/admin/all`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRequirements(result.requirements || []);
      } else {
        toast.error('Failed to fetch document requirements');
      }
    } catch (error) {
      console.error('Error fetching requirements:', error);
      toast.error('Failed to fetch document requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRequirement = async () => {
    try {
      const url = editingRequirement
        ? `${API_URL}/document-requirements/admin/${editingRequirement._id}`
        : `${API_URL}/document-requirements/admin/create`;
      
      const method = editingRequirement ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(`Document requirement ${editingRequirement ? 'updated' : 'created'} successfully`);
        setDialogOpen(false);
        setEditingRequirement(null);
        resetForm();
        fetchRequirements();
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to save requirement');
      }
    } catch (error) {
      console.error('Error saving requirement:', error);
      toast.error('Failed to save requirement');
    }
  };

  const handleDeleteRequirement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this requirement?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/document-requirements/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        toast.success('Document requirement deleted successfully');
        fetchRequirements();
      } else {
        toast.error('Failed to delete requirement');
      }
    } catch (error) {
      console.error('Error deleting requirement:', error);
      toast.error('Failed to delete requirement');
    }
  };

  const handleEditRequirement = (requirement) => {
    setEditingRequirement(requirement);
    setFormData({
      country: requirement.country,
      documentId: requirement.documentId,
      label: requirement.label,
      description: requirement.description,
      required: requirement.required,
      category: requirement.category,
      order: requirement.order,
      imageOnly: requirement.imageOnly,
      internRequired: requirement.internRequired ?? false,
    });
    setDialogOpen(true);
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('This will replace all existing requirements with default ones. Are you sure?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/document-requirements/admin/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        toast.success('Default requirements initialized successfully');
        fetchRequirements();
      } else {
        toast.error('Failed to initialize default requirements');
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      toast.error('Failed to initialize default requirements');
    }
  };

  const resetForm = () => {
    setFormData({
      country: 'India',
      documentId: '',
      label: '',
      description: '',
      required: true,
      category: 'Other',
      order: 0,
      imageOnly: false,
      internRequired: false,
    });
  };

  const getRequirementsByCountry = (country) => {
    return requirements
      .filter(req => req.country === country)
      .sort((a, b) => a.order - b.order || new Date(a.createdAt) - new Date(b.createdAt));
  };

  const getCategoryColor = (category) => {
    const colors = {
      Identity: 'primary',
      Education: 'secondary',
      Financial: 'success',
      Policy: 'warning',
      Experience: 'info',
      Other: 'default',
    };
    return colors[category] || 'default';
  };

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '30px' ,fontWeight:600,lineHeight:1.5 ,letterSpacing:1 }}>
            Document Requirements Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRequirements}
              disabled={loading}
              sx={{ fontSize: '14px' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<InitializeIcon />}
              onClick={handleInitializeDefaults}
              color="warning"
              sx={{ fontSize: '14px' }}
            >
              Initialize Defaults
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setEditingRequirement(null);
                setDialogOpen(true);
              }}
              sx={{ fontSize: '14px' }}
            >
              Add Requirement
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Tabs
              value={selectedCountry}
              onChange={(e, newValue) => setSelectedCountry(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              {COUNTRIES.map((country) => (
                <Tab
                  key={country}
                  label={`${country} (${getRequirementsByCountry(country).length})`}
                  value={country}
                  sx={{ fontSize: '16px' }}
                />
              ))}
            </Tabs>

            <Grid container spacing={3}>
              {getRequirementsByCountry(selectedCountry).map((requirement) => (
                <Grid item xs={12} md={6} lg={4} key={requirement._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: requirement.isActive ? 1 : 0.6,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div" gutterBottom sx={{ fontSize: '16px' }}>
                          {requirement.label}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={requirement.category}
                            size="small"
                            color={getCategoryColor(requirement.category)}
                          />
                          {requirement.required && (
                            <Chip label="Required" size="small" color="error" />
                          )}
                          {requirement.imageOnly && (
                            <Chip label="Image Only" size="small" color="warning" />
                          )}
                          {requirement.internRequired && (
                            <Chip label="Intern" size="small" color="secondary" />
                          )}
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px' }}>
                        {requirement.description}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                        ID: {requirement.documentId} | Order: {requirement.order}
                      </Typography>
                    </CardContent>
                    
                    <CardActions>
                      <IconButton
                        size="small"
                        onClick={() => handleEditRequirement(requirement)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRequirement(requirement._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {getRequirementsByCountry(selectedCountry).length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No document requirements found for {selectedCountry}. 
                Click "Add Requirement" to create one or "Initialize Defaults" to load default requirements.
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRequirement ? 'Edit Document Requirement' : 'Add Document Requirement'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  label="Country"
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Document ID"
                value={formData.documentId}
                onChange={(e) => setFormData({ ...formData, documentId: e.target.value })}
                helperText="Unique identifier (e.g., pan_card, passport)"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                helperText="Display name for the document"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                helperText="Detailed description of the document"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                helperText="Display order (lower numbers appear first)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                  />
                }
                label="Required Document"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.imageOnly}
                    onChange={(e) => setFormData({ ...formData, imageOnly: e.target.checked })}
                  />
                }
                label="Image Only (No PDF allowed)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.internRequired}
                    onChange={(e) => setFormData({ ...formData, internRequired: e.target.checked })}
                  />
                }
                label="Required for Interns"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveRequirement}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.documentId || !formData.label || !formData.description}
          >
            {editingRequirement ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDocumentRequirements;
